import { useContent } from "@/context/ContentContext";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SubscribeForm from "@/components/SubscribeForm";

function coverSrc(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function Blog() {
  const { posts } = useContent();

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="blog-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-6">[ 05 · insights ]</div>
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <h1 className="lg:col-span-8 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          Insights — field-tested tips for cyber resilience.
        </h1>
        <div className="lg:col-span-4">
          <SubscribeForm variant="inline" />
        </div>
      </div>

      <div className="mt-16 grid gap-6 md:grid-cols-12">
        {posts.length === 0 && (
          <div className="md:col-span-12 p-10 border border-border rounded-2xl text-muted-foreground font-mono text-sm">
            [ no posts yet — check back soon ]
          </div>
        )}
        {posts.map((p, i) => {
          const cover = coverSrc(p.cover_image);
          const isFeatured = i === 0;
          return (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className={`group relative rounded-2xl border border-border bg-card overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300 ${
                isFeatured ? "md:col-span-8" : i === 1 ? "md:col-span-4" : "md:col-span-6"
              }`}
              data-testid={`blog-card-${i}`}
            >
              {cover && (
                <Link to={`/insights/${p.slug}`} className="block overflow-hidden">
                  <img
                    src={cover}
                    alt=""
                    className={`w-full object-cover group-hover:scale-[1.02] transition-transform duration-500 ${isFeatured ? "h-72" : "h-48"}`}
                  />
                </Link>
              )}
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">{p.tag}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                </div>
                <h2 className={`font-serif tracking-tight ${isFeatured ? "text-3xl sm:text-4xl" : "text-2xl"}`}>{p.title}</h2>
                <p className="mt-4 text-muted-foreground text-sm leading-relaxed">{p.excerpt}</p>
                <Link
                  to={`/insights/${p.slug}`}
                  className="mt-6 inline-flex items-center gap-1 font-mono text-xs uppercase tracking-[0.2em] link-underline"
                  data-testid={`blog-read-more-${i}`}
                >
                  Read <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.article>
          );
        })}
      </div>

      <div className="mt-24">
        <SubscribeForm />
      </div>
    </div>
  );
}
